/**
 * @file Singleton Valtio store for the AI Mapping Review panel. Holds the one
 * active review session (per pending apply-tool approval, identified by
 * `approval.toolCallId`) plus panel chrome state (open/expanded — the panel's
 * width is not here: `uiState.mappingReviewPanel` owns and persists it). A
 * session is always staged — the client loads `parsed/labeled.json` +
 * `process/<n>.json` itself and edits a live copy; user decisions are derived
 * as a diff against the untouched `baseDoc` at save time
 * (`buildEditedProcessDocument`). The session is discriminated by the loaded
 * doc's `target.kind`: an `entityRecords` import edits a single table slice,
 * a whole `document` edits header fields, references, and one table slice per
 * `TablePlan` (keyed by its group id). Bulky immutable source data (`chunks`,
 * `baseDoc`) is installed wrapped in valtio `ref()` so large payloads carry no
 * proxy cost; the sparse live edit records stay proxied for granular
 * reactivity. Sessions are seeded and mutated exclusively through the actions
 * in `mappingReviewSession.ts`; UI components subscribe via `useSnapshot` on
 * narrow sub-proxies.
 */
import { proxy } from 'valtio';
import type {
  AgenticApprovalRequiredEvent,
  ColumnAlternative,
  ColumnPlan,
  DateLocale,
  LocaleProvenance,
  MappingTarget,
  NumberLocale,
  ProcessDocument,
  RowClassification,
  TableChunk,
  TableReferenceValueResolution,
} from '#types';

/** Sparse row selection: `Record` keys for granular reactivity, O(1) count. */
export interface MappingReviewSelection {
  selected: Record<number, true>;
  count: number;
  lastClicked: number | null;
}

/**
 * Live, editable state for one table's two Typesafe axes — shared by an
 * `entityRecords` import (one slice) and each `TablePlan` of a whole document
 * (one slice per group id). `chunks` are the resolved source rows for this
 * slice (`ref()`d, immutable); `baseColumns`/`baseRows` are the untouched
 * plan axes (`ref()`d) the decisions diff is built against; `columns` is the
 * live edited copy. `targetTableId`/`targetRemoved` are document-only: the
 * per-table retarget / remove decision (always undefined/false for imports).
 */
export interface MappingReviewTableSlice {
  /** Table group id (`TablePlan.ref.id`); the import slice uses the doc's `source.ref.id`. */
  groupId: string;
  /** Resolved selection's table chunks — `ref()`d, immutable source rows. */
  chunks: TableChunk[];
  /** Untouched plan column axis — `ref()`d. */
  baseColumns: ColumnPlan[];
  /**
   * Untouched per-target ranked source columns the model judged but left
   * unmapped, keyed by target field — `ref()`d. Feeds the column-axis transpose
   * the review UI ranks against; stale entries self-neutralize via the
   * universe intersection after a retarget.
   */
  unmappedTargets: Record<string, ColumnAlternative[]>;
  /** Untouched plan row classifications — `ref()`d. */
  baseRows: RowClassification[];
  /** Live edited column plans (deep copy of `baseColumns` + user edits), proxied. */
  columns: ColumnPlan[];
  /** Row indexes the user rejected — skipped at write, index reserved. */
  rejectedRows: Record<number, true>;
  /** rowIndex → sourceColumn → raw value override, applied before cleaning rules. */
  cellOverrides: Record<number, Record<string, string | number | null>>;
  selection: MappingReviewSelection;
  /** `label.id` of the chunk in view; `null` = all chunks combined. */
  activeChunkId: string | null;
  /** Document tables only: the target table id (retargeted when ≠ plan's). */
  targetTableId: string;
  /** Document tables only: this group is excluded from the apply. */
  targetRemoved: boolean;
  /**
   * Numeric-format convention from the staged doc — passed to the cleaning-rule
   * preview so before/after cells match what the locale-aware apply writes.
   */
  locale?: NumberLocale;
  /**
   * Date-order convention from the staged doc — mirrors `locale` for the
   * `coerceDate` cleaning-rule preview.
   */
  dateLocale?: DateLocale;
  /**
   * Two-digit-year window pivot from the staged doc — mirrors `dateLocale`,
   * threaded into the `coerceDate` cleaning-rule + `coerceCellValue` preview.
   */
  centuryPivot?: number;
  /**
   * Fixed UTC offset (`'+05:30'`/`'Z'`/`'naive'`) applied to a zone-less
   * date-time — mirrors `dateLocale` for the date/dateTime preview.
   */
  dateZone?: string;
}

/** Per-header-field live edit state, keyed by `targetField`. */
export interface MappingReviewFieldLive {
  targetField: string;
  /** Index into `baseDoc.facts` of the chosen fact (re-pickable). */
  factIndex: number;
  /** Live value (the chosen fact's value, or a user edit). */
  value: string | number | null;
  removed: boolean;
  /** True once the user re-picked the fact, edited the value, or added the field. */
  manual: boolean;
}

/** Per-reference-slot live edit state, keyed by `slotKey`. */
export interface MappingReviewReferenceLive {
  slotKey: string;
  entityId: string;
  entityGroupId?: string;
  /** Index into `baseDoc.facts` of the chosen fact; `-1` for an added slot. */
  factIndex: number;
  /** The live value→record resolution. */
  resolution: TableReferenceValueResolution;
  removed: boolean;
  /** True once the user overrode the resolution, re-picked the fact, or added the slot. */
  manual: boolean;
}

/**
 * The single review session. `target.kind` discriminates the surface: an
 * `entityRecords` import uses only the `import` slice (and `fields`/
 * `references`/`tables` stay empty), a whole `document` uses `fields`,
 * `references`, and `tables` (and `import` stays null).
 */
export interface MappingReviewSession {
  approval: AgenticApprovalRequiredEvent;
  fileFolder: string;
  processIndex: number;
  target: MappingTarget | null;
  /** The persisted process document as loaded — `ref()`d, never mutated. */
  baseDoc: ProcessDocument | null;
  /** entityRecords import: the single editable table slice. Null for document docs. */
  import: MappingReviewTableSlice | null;
  /** document: live header-field edits, keyed by targetField. Empty for imports. */
  fields: Record<string, MappingReviewFieldLive>;
  /** document: live reference edits, keyed by slotKey. Empty for imports. */
  references: Record<string, MappingReviewReferenceLive>;
  /** document: one editable table slice per `TablePlan`, keyed by group id. Empty for imports. */
  tables: Record<string, MappingReviewTableSlice>;
  /** document: stable order of `tables` keys (TablePlan order). Empty for imports. */
  tableOrder: string[];
  /** Reviewer-chosen numeric-format convention, propagated to every slice's preview. */
  locale?: NumberLocale;
  /** Reviewer-chosen date-order convention, propagated to every slice's preview. */
  dateLocale?: DateLocale;
  /** Reviewer-chosen two-digit-year window pivot, propagated to every slice's preview. */
  centuryPivot?: number;
  /** Reviewer-chosen fixed UTC offset for zone-less date-times, propagated to every slice's preview. */
  dateZone?: string;
  /** Per-axis provenance of the localization decisions — `'user'` on the axis the reviewer sets. */
  localeSource?: LocaleProvenance;
  /**
   * Text-encoding metadata carried from the staged doc for a read-only status
   * badge: the applied label and an auto-detect failure flag. A load-time fact,
   * never user-editable in review.
   */
  encoding?: { chosen?: string; failed?: true; candidates?: string[] };
  dirty: boolean;
  loading: boolean;
  loadError: string | null;
  saving: boolean;
}

export const mappingReviewState = proxy<{
  session: MappingReviewSession | null;
  panel: { open: boolean; expanded: boolean };
}>({
  session: null,
  panel: { open: false, expanded: false },
});
